import { z } from 'zod';
import { PageQuerySchema, UuidSchema } from './common';
import { OutboxStatusSchema, OutboxTargetSchema } from './enums';

/** §13, §17.2 ТЗ, E7/E10. Раздел «Интеграции и журнал ошибок». */

export const BreakerStateSchema = z.enum(['CLOSED', 'OPEN', 'HALF_OPEN']);
export type BreakerStateValue = z.infer<typeof BreakerStateSchema>;

export const IntegrationStateSchema = z.object({
  /** есть ли интеграция вообще (в автономном режиме — false) */
  integrationEnabled: z.boolean(),
  /** доступна ли она прямо сейчас (E10: circuit breaker замкнут) */
  b24Available: z.boolean(),
  breaker: z.object({
    state: BreakerStateSchema,
    openedAt: z.string().datetime().nullable(),
    nextProbeAt: z.string().datetime().nullable(),
    /** безопасная причина, без секретов (§18) */
    reason: z.string().nullable(),
  }),
  /** очередь по статусам; все статусы присутствуют, хотя бы нулём */
  counts: z.object({
    PENDING: z.number().int().min(0),
    IN_PROGRESS: z.number().int().min(0),
    DONE: z.number().int().min(0),
    FAILED: z.number().int().min(0),
    NEEDS_ATTENTION: z.number().int().min(0),
    NEEDS_RECONCILIATION: z.number().int().min(0),
  }),
  /** E7: счётчик «Требует внимания» в шапке = NEEDS_ATTENTION + NEEDS_RECONCILIATION */
  attentionCount: z.number().int().min(0),
  /** null — очередь пуста; 0 — команда моложе секунды. Это разные сообщения. */
  oldestPendingAgeSeconds: z.number().int().min(0).nullable(),
});
export type IntegrationState = z.infer<typeof IntegrationStateSchema>;

/** Payload наружу не отдаётся: в SEND_MESSAGE лежит текст сообщения (§18). */
export const OutboxEntrySchema = z.object({
  id: UuidSchema,
  target: OutboxTargetSchema,
  command: z.string(),
  status: OutboxStatusSchema,
  attempts: z.number().int().min(0),
  dedupKey: z.string(),
  /** §18: безопасное описание последней ошибки */
  lastError: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  correlationId: z.string().nullable(),
  nextAttemptAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type OutboxEntry = z.infer<typeof OutboxEntrySchema>;

export const ListOutboxQuerySchema = PageQuerySchema.extend({
  status: OutboxStatusSchema.optional(),
  target: OutboxTargetSchema.optional(),
  command: z.string().max(100).optional(),
  entityType: z.string().max(100).optional(),
  entityId: z.string().max(100).optional(),
});
export type ListOutboxQuery = z.infer<typeof ListOutboxQuerySchema>;

/**
 * Блок «Интеграция» в карточке инцидента и обращения (§13, сценарий 34):
 * статус команды, попытки, безопасная ошибка, кнопка «Повторить» для SYS_ADMIN.
 */
export const EntityIntegrationSchema = z.object({
  outboxId: UuidSchema,
  command: z.string(),
  status: OutboxStatusSchema,
  attempts: z.number().int().min(0),
  lastError: z.string().nullable(),
  nextAttemptAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});
export type EntityIntegration = z.infer<typeof EntityIntegrationSchema>;

/**
 * §17.3 ТЗ. Сверка: бизнес-записи без ожидаемой проекции в Bitrix24.
 *
 * Отчёт строится от бизнес-правила, а не от очереди: команда могла не
 * поставиться вовсе — например, объект завели до включения интеграции, — и
 * тогда в журнале ошибок нет ни строки, а проекции нет. Массовой автоматической
 * сверки всех данных портала в объём не входит (§17.3).
 */
export const ProjectionKindSchema = z.enum(['CRM_CONTACT', 'LIST_ROW', 'TASK']);
export type ProjectionKind = z.infer<typeof ProjectionKindSchema>;

export const ReconciliationEntitySchema = z.enum([
  'person',
  'project_admin',
  'incident',
  'customer_request',
]);
export type ReconciliationEntity = z.infer<typeof ReconciliationEntitySchema>;

export const ReconciliationRowSchema = z.object({
  entityType: ReconciliationEntitySchema,
  entityId: UuidSchema,
  /** Как запись названа в админке: номер инцидента или обращения, ФИО персоны. */
  title: z.string(),
  projectId: UuidSchema.nullable(),
  projectName: z.string().nullable(),
  expected: ProjectionKindSchema,
  /** С какого момента проекция ожидается: регистрация, создание инцидента. */
  expectedSince: z.string().datetime(),
  /** Последняя команда по записи; `null` — команды нет вовсе. */
  outbox: EntityIntegrationSchema.nullable(),
  /** Безопасное объяснение, почему проекции нет (§18). */
  reason: z.string(),
});
export type ReconciliationRow = z.infer<typeof ReconciliationRowSchema>;

export const ReconciliationReportSchema = z.object({
  integrationEnabled: z.boolean(),
  /** E8: списки выключены настройкой — строк списка не ждём, это не ошибка. */
  listsDisabled: z.boolean(),
  /** Записи моложе этого возраста расхождением не считаются: команда ещё в очереди. */
  graceMinutes: z.number().int().min(0),
  counts: z.object({
    CRM_CONTACT: z.number().int().min(0),
    LIST_ROW: z.number().int().min(0),
    TASK: z.number().int().min(0),
    total: z.number().int().min(0),
  }),
  items: z.array(ReconciliationRowSchema),
  /** Строк больше, чем отдано. Счётчики при этом полные: обрезка не молчаливая. */
  truncated: z.boolean(),
});
export type ReconciliationReport = z.infer<typeof ReconciliationReportSchema>;

export const ReconciliationQuerySchema = z.object({
  projectId: UuidSchema.optional(),
  expected: ProjectionKindSchema.optional(),
  graceMinutes: z.coerce.number().int().min(0).max(10_080).default(15),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type ReconciliationQuery = z.infer<typeof ReconciliationQuerySchema>;

/**
 * §17, §17.3. Ручная постановка команды на проекцию из отчёта сверки.
 *
 * Для записей, у которых команды нет вовсе: запись появилась до включения
 * интеграции либо команда потеряна. У такого расхождения не было ни одного
 * действия — отчёт ставил задачу и не давал её решить (QA 22.08.2026, дефект 5).
 */
export const ReconciliationEnqueueBodySchema = z.object({
  entityType: ReconciliationEntitySchema,
  entityId: UuidSchema,
});
export type ReconciliationEnqueueBody = z.infer<typeof ReconciliationEnqueueBodySchema>;

/**
 * §17.2, §17.3, сценарий 35. Разбор состояния «Требует сверки».
 *
 * Система не смогла узнать, применилась ли операция, и по §17.2 не имеет права
 * гадать. Ответ даёт человек, посмотревший портал; система лишь записывает его
 * решение и то, чем оно подтверждено.
 */
export const ResolveOutboxBodySchema = z.object({
  /** `APPLIED` — операция в портале нашлась; `NOT_APPLIED` — её там нет. */
  decision: z.enum(['APPLIED', 'NOT_APPLIED']),
  /** Идентификатор найденного: номер задачи, контакта, строки списка. */
  externalId: z.string().min(1).max(64).optional(),
  /** Чем подтверждена проверка. Попадает в аудит (§18: без ПДн и секретов). */
  note: z.string().max(500).optional(),
});
export type ResolveOutboxBody = z.infer<typeof ResolveOutboxBodySchema>;

/**
 * §15.1, T40. Как портал Bitrix24 видит наше приложение.
 *
 * Раздел появился по разбору 21.08.2026: пункт левого меню не открывался у
 * рядового сотрудника, и двое суток это выглядело нашей поломкой. Причина была
 * снаружи — приложение в статусе `PRIVATE`, а такое видит только его автор.
 * Система этого состояния не читала и показать не могла, поэтому искали не там.
 *
 * Замок на видимости не один: у приложения есть ещё список сотрудников, и он
 * живёт только в интерфейсе портала — метода REST для него нет, шлюз о нём не
 * знает, и сюда он не попадает. Раздел показывает то, что видно снаружи, и
 * говорит об этом прямо, а не выдаёт `PUBLISHED` за «сотрудник войдёт».
 *
 * Это диагностика, а не логика: §4.2 КП запрещает держать бизнес-логику в
 * VibeCode, но не запрещает спросить у шлюза, что он о нас знает.
 */
export const CatalogStatusSchema = z.enum(['PRIVATE', 'PUBLISHED', 'UNPUBLISHED']);
export type CatalogStatus = z.infer<typeof CatalogStatusSchema>;

export const EmbeddingPlacementSchema = z.object({
  /** Код места: `LEFT_MENU`, `CRM_DEAL_DETAIL_TAB` и подобные. */
  code: z.string(),
  /**
   * Адрес обработчика по данным портала. `null` — портал о нём не спрашивали:
   * подробности мест отдаются только пользовательской сессии, а у сервера её нет.
   */
  handler: z.string().nullable(),
  title: z.string().nullable(),
  /**
   * Портал считает привязку испорченной. `null` — не проверялось, и это
   * третье состояние, а не «всё хорошо».
   */
  misbound: z.boolean().nullable(),
});
export type EmbeddingPlacement = z.infer<typeof EmbeddingPlacementSchema>;

export const EmbeddingStateSchema = z.object({
  /** Настроен ли вход через портал. Ложь — режим не встроенный, читать нечего. */
  configured: z.boolean(),
  /**
   * Шлюз не ответил. Пустая карточка и «шлюз недоступен» — разные новости, и
   * сводить их к прочерку нельзя (тот же довод, что у `oldestPendingAgeSeconds`).
   */
  unavailable: z.boolean(),
  appTitle: z.string().nullable(),
  bitrixClientId: z.string().nullable(),
  /** Главное поле раздела: `PRIVATE` — приложение видит только его автор. */
  catalogStatus: CatalogStatusSchema.nullable(),
  publishedAt: z.string().datetime().nullable(),
  /** Адрес приложения по записи шлюза. */
  appUrl: z.string().nullable(),
  /** Наш `APP_BASE_URL` — рядом, потому что шлюз сверяет их посимвольно (S3). */
  appBaseUrl: z.string(),
  /** Расхождение этих двух адресов даёт `400 INVALID_REDIRECT_URI` при входе. */
  appUrlMatches: z.boolean(),
  placements: z.array(EmbeddingPlacementSchema),
  /** Ответ шлюза о сверке с порталом: `ok`, `unknown` и прочие его значения. */
  portalSync: z.string().nullable(),
  /** Почему сверка не состоялась, словами шлюза. Открытый набор строк. */
  portalSyncReason: z.string().nullable(),
  /** Когда снят снимок: карточка кешируется, и её возраст виден человеку. */
  readAt: z.string().datetime(),
});
export type EmbeddingState = z.infer<typeof EmbeddingStateSchema>;
